/**
 * Reading a `PartRef` back off a picked mesh.
 *
 * The reference is written onto `Mesh.userData` during rendering and narrowed
 * here with a type guard, so picking never needs a type assertion. The type
 * itself lives in the model layer.
 */

import type { PartRef, PartType } from "../model/types.ts";

export type { PartRef, PartType };

const PART_TYPES = new Set<string>([
  "leg",
  "beam",
  "diag",
  "stair",
  "ladder",
  "rail",
  "toe",
  "bracket",
  "board",
  "deck",
  "plank",
  "freeEnd",
]);

function hasPartType(value: Record<string, unknown>): value is Record<string, unknown> & { type: PartType } {
  return typeof value.type === "string" && PART_TYPES.has(value.type);
}

/** Narrow a mesh's `userData` to a PartRef, or null when it carries none. */
export function readPartRef(userData: Record<string, unknown>): PartRef | null {
  if (!hasPartType(userData)) {
    return null;
  }

  const { type } = userData;
  if (type === "leg" && typeof userData.key === "string") {
    return userData.top === true ? { type, key: userData.key, top: true } : { type, key: userData.key };
  }
  if (type === "freeEnd") {
    const { x, z, p } = userData;
    if (typeof x === "number" && typeof z === "number" && typeof p === "number") {
      return { type, x, z, p };
    }
    return null;
  }
  if (type === "plank") {
    const { idx, slot, fam } = userData;
    if (typeof idx === "number" && typeof slot === "number" && typeof fam === "number") {
      return { type, idx, slot, fam };
    }
    return null;
  }
  if (type === "leg") {
    return null;
  }

  return typeof userData.idx === "number" ? { type, idx: userData.idx } : null;
}

/** Identity of the whole part a mesh belongs to — every mesh sharing it highlights together. */
export function elemId(ref: PartRef): string {
  if (ref.type === "leg") {
    return ref.top ? `leg:${ref.key}:top` : `leg:${ref.key}`;
  }
  if (ref.type === "deck" || ref.type === "plank") {
    return `deck:${ref.idx}`;
  }
  if (ref.type === "freeEnd") {
    return `freeEnd:${ref.x},${ref.z},${ref.p}`;
  }

  return `${ref.type}:${ref.idx}`;
}

/**
 * Do these two references highlight together?
 *
 * Hovering the removable top piece of a stacked standard lights only that
 * piece, because that is all a click would take off. Hovering anywhere else on
 * the standard lights the whole thing, top piece included.
 */
export function sameHighlightGroup(target: PartRef, candidate: PartRef): boolean {
  if (target.type === "leg") {
    if (candidate.type !== "leg" || candidate.key !== target.key) {
      return false;
    }

    return target.top ? candidate.top === true : true;
  }

  return elemId(target) === elemId(candidate);
}
